import React from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import Close from "@material-ui/icons/Close";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import { FAR_LINK_RECIPIENT, FAR_ENFORCEMENT, FAR_EXCLUSION, formatFarMessage } from "../../../../assets/js/utils/far/farEngine";
import farDialogStyle from "../../../../assets/jss/user/far/farDialogStyle.jsx";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";
import { FarNotice } from "./farUi";

const REASON_STORAGE_KEY = "sendlinx_far_last_reason";

const useStyles = makeStyles(theme => {
    const base = farDialogStyle(theme);
    const s = farShared(theme);
    const t = s.t;
    return Object.assign(base, {
        list: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "10px" },
        item: {
            display: "flex", gap: "12px", alignItems: "flex-start", padding: "12px", borderRadius: "10px",
            border: "1px solid "+t.danger.border, backgroundColor: t.danger.bg
        },
        itemIcon: { color: t.danger.fg, flexShrink: 0, marginTop: "1px" },
        itemBody: { flex: 1, minWidth: 0 },
        recipient: { margin: 0, fontWeight: 700, fontSize: "15px", color: t.textStrong, wordBreak: "break-all" },
        pairs: { margin: "4px 0 0", padding: 0, listStyle: "none", fontSize: "13px", color: t.text, "& li": { wordBreak: "break-word" }, "& em": { fontStyle: "normal", color: t.textMuted } },
        removeOne: { ...base.danger, minHeight: "34px", padding: "0 12px", fontSize: "13px", flexShrink: 0 },
        message: { margin: 0, padding: "12px 14px", borderRadius: "8px", backgroundColor: t.surfaceAlt, border: "1px solid "+t.border, whiteSpace: "pre-wrap", fontSize: "14px", lineHeight: "20px" },
        divider: { border: "none", borderTop: "1px solid "+t.border, margin: 0 },
        actionsRow: { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" },
        option: { display: "flex", flexDirection: "column", gap: "10px", padding: "14px", borderRadius: "10px", border: "1px solid "+t.border },
        optionTitle: { margin: 0, fontSize: "15px", fontWeight: 700, color: t.textStrong },
        optionText: { margin: 0, fontSize: "13px", color: t.textMuted, lineHeight: "18px" },
        blockedTag: {
            display: "inline-block", margin: "4px 0 0", padding: "2px 8px", borderRadius: "999px", fontSize: "12px", fontWeight: 700,
            backgroundColor: t.danger.solid, color: t.danger.onSolid
        },
        blockedWhy: { margin: "4px 0 0", fontSize: "13px", color: t.text, "& em": { fontStyle: "normal", color: t.textMuted } }
    });
});

/**
 * Blocking review shown when files match a rule but a recipient is not authorized.
 * Actions: Remove recipient (safest) · Authorize and Continue · Ignore and Continue · Cancel.
 *
 * Recipients blocked by a rule exclusion are marked "Blocked by FAR exclusion":
 *   block          remove only (no override, not even "warn only")
 *   admin_approval an administrator authorizes with a reason; members see a notice
 *   global_policy  the normal options below
 * Authorizing only applies to pairs this sender may override (pair.overridable).
 * Authorizing returns the user to the Send page; it never sends by itself
 * (except "Acknowledge and Send" in Warn-only mode, which is the Send action).
 */
function FarExceptionModal({ open, evaluation, canOverride, isAdmin, acknowledgeMode, transferType, onClose, onRemoveRecipients, onResolve }){
    const classes = useStyles();
    const settings = evaluation ? evaluation.settings : {};
    const [reason, setReason] = React.useState(() => {
        if(settings.remember_override_reason){
            try{ return localStorage.getItem(REASON_STORAGE_KEY) || ""; }catch(e){ }
        }
        return "";
    });
    const [confirmIgnore, setConfirmIgnore] = React.useState(false);
    const [reasonError, setReasonError] = React.useState("");

    if(!evaluation){
        return null;
    }

    const pairs = evaluation.unresolvedPairs;
    const byRecipient = {};
    pairs.forEach(pair => {
        if(!byRecipient[pair.recipient]){ byRecipient[pair.recipient] = []; }
        byRecipient[pair.recipient].push(pair);
    });
    const recipients = Object.keys(byRecipient);
    const removable = recipients.filter(r => r !== FAR_LINK_RECIPIENT);
    const isBlock = settings.enforcement === FAR_ENFORCEMENT.BLOCK;
    const excludedRecipients = evaluation.excludedRecipients || {};
    const excludedCount = recipients.filter(r => excludedRecipients[r]).length;
    const overridablePairs = pairs.filter(p => p.overridable);
    const fixedRecipients = recipients.filter(r => byRecipient[r].some(p => !p.overridable));
    const needsAdminApproval = overridablePairs.some(p => p.excluded && p.effectiveExclusionAction === FAR_EXCLUSION.ADMIN);
    const canAuthorize = overridablePairs.length > 0;
    const allowIgnore = canOverride && !isBlock && settings.allow_ignore !== false && !acknowledgeMode && !needsAdminApproval;
    const reasonRequired = settings.require_override_reason !== false || needsAdminApproval;

    const first = pairs[0];
    const firstFile = first ? evaluation.files.find(f => f.path === first.filePath && f.name === first.fileName) : null;
    const message = !first ? "" : first.excluded ?
        "The file \""+first.fileName+"\" matches the File Attachment Rule \""+first.ruleName+"\". "+first.recipient+" is blocked by a FAR exclusion ("+(first.exclusionType === "domain" ? "blocked domain "+first.exclusionValue : "blocked address")+")."
        : formatFarMessage(settings.default_message, {
            file: first.fileName,
            rule: first.ruleName,
            recipients: firstFile && firstFile.allowedRecipients.length > 0 ? firstFile.allowedRecipients.join(", ") : "—"
        });
    const exclusionHelp = (exclusion) => {
        if(exclusion.action === FAR_EXCLUSION.BLOCK){ return "Must be removed. This exclusion can't be overridden."; }
        if(exclusion.action === FAR_EXCLUSION.ADMIN){ return isAdmin ? "Requires your authorization as administrator, with a reason." : "Requires administrator authorization."; }
        return "Follows your organization's exception settings.";
    };

    const submit = (action) => {
        const clean = reason.trim();
        if(action !== "ignored" && reasonRequired && clean.length <= 0){
            setReasonError("Enter a reason for this exception.");
            const el = document.getElementById("far-exception-reason");
            if(el){ el.focus(); }
            return;
        }
        if(settings.remember_override_reason && clean.length > 0){
            try{ localStorage.setItem(REASON_STORAGE_KEY, clean); }catch(e){ }
        }
        onResolve({ action, reason: clean });
    };

    const isLink = transferType === "link";
    const notAuthorizedCount = recipients.length - excludedCount;
    const subtitle = isLink ?
        "Share links can't confirm who opens them, and these files are restricted to named recipients."
        : [
            excludedCount > 0 ? excludedCount+" recipient"+(excludedCount === 1 ? " is" : "s are")+" blocked by a FAR exclusion" : null,
            notAuthorizedCount > 0 ? notAuthorizedCount+" recipient"+(notAuthorizedCount === 1 ? " is" : "s are")+" not authorized" : null
        ].filter(Boolean).join(" and ")+" for the files you're sending.";

    return (
        <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper+" "+classes.wide }} aria-labelledby="far-exception-title" aria-describedby="far-exception-subtitle" scroll="body">
            <div className={classes.header}>
                <div>
                    <h2 className={classes.title} id="far-exception-title">{acknowledgeMode ? "File Attachment Rules warning" : "Review File Attachment Rules"}</h2>
                    <p className={classes.subtitle} id="far-exception-subtitle">{subtitle}</p>
                </div>
                <button type="button" className={classes.close} aria-label="Close" onClick={onClose}><Close /></button>
            </div>
            <div className={classes.body}>
                { message ? <p className={classes.message}>{message}</p> : null }
                <ul className={classes.list} aria-label="Recipients that need attention">
                    {
                        recipients.map(recipient => (
                            <li className={classes.item} key={recipient}>
                                <ErrorOutline className={classes.itemIcon} aria-hidden="true" />
                                <div className={classes.itemBody}>
                                    <p className={classes.recipient}>{recipient === FAR_LINK_RECIPIENT ? "Share link (anyone with the link)" : recipient}</p>
                                    {
                                        excludedRecipients[recipient] ?
                                            <>
                                                <span className={classes.blockedTag}>Blocked by FAR exclusion</span>
                                                <p className={classes.blockedWhy}>
                                                    {excludedRecipients[recipient].type === "domain" ? "Blocked domain "+excludedRecipients[recipient].value : "Blocked email address"}
                                                    <em> — rule "{excludedRecipients[recipient].ruleName}". {exclusionHelp(excludedRecipients[recipient])}</em>
                                                </p>
                                            </>
                                        : null
                                    }
                                    <ul className={classes.pairs}>
                                        {
                                            byRecipient[recipient].slice(0, 6).map(pair => (
                                                <li key={pair.key}>{pair.fileName} <em>— rule "{pair.ruleName}"</em></li>
                                            ))
                                        }
                                        { byRecipient[recipient].length > 6 ? <li><em>…and {byRecipient[recipient].length - 6} more</em></li> : null }
                                    </ul>
                                </div>
                                {
                                    recipient !== FAR_LINK_RECIPIENT ?
                                        <button type="button" className={classes.removeOne} onClick={() => onRemoveRecipients([recipient])} aria-label={"Remove recipient "+recipient}>Remove</button>
                                    : null
                                }
                            </li>
                        ))
                    }
                </ul>
                {
                    removable.length > 1 ?
                        <div className={classes.actionsRow}>
                            <button type="button" className={classes.accent} onClick={() => onRemoveRecipients(removable)}>Remove all {excludedCount > 0 ? "blocked and unauthorized" : "unauthorized"} recipients</button>
                        </div>
                    : null
                }

                {
                    canAuthorize && fixedRecipients.length > 0 ?
                        <FarNotice tone="warning">
                            {fixedRecipients.join(", ")} can't be authorized{ isAdmin ? "" : " by you" }. Remove {fixedRecipients.length === 1 ? "this recipient" : "these recipients"} to send. Authorizing below applies to the other recipients only.
                        </FarNotice>
                    : null
                }
                {
                    canAuthorize ?
                        <>
                            <hr className={classes.divider} />
                            <div className={classes.option}>
                                <p className={classes.optionTitle}>{acknowledgeMode ? "Send anyway" : (needsAdminApproval ? "Authorize as administrator" : "Authorize an exception")}</p>
                                <p className={classes.optionText}>
                                    { acknowledgeMode ?
                                        "Your organization allows sending after a warning. This will be recorded with your name in the FAR audit log."
                                        : needsAdminApproval ?
                                        "A rule requires administrator authorization to send to an excluded recipient. Allow it for this transfer only; you'll return to the Send page to review and send. Your name and reason are recorded in the FAR audit log."
                                        : "Allow these recipients for this transfer only. You'll return to the Send page to review and send. This is recorded with your name in the FAR audit log." }
                                </p>
                                <div className={classes.field}>
                                    <label className={classes.label} htmlFor="far-exception-reason">
                                        Reason{ reasonRequired ? <span className={classes.required} aria-hidden="true">*</span> : " (optional)" }
                                    </label>
                                    <textarea id="far-exception-reason" className={classes.textarea+" "+(reasonError ? classes.inputError : "")} value={reason} maxLength={500}
                                        onChange={(e) => { setReason(e.target.value); if(reasonError){ setReasonError(""); } }}
                                        placeholder="e.g. Customer asked us to copy their US office"
                                        aria-required={reasonRequired ? "true" : "false"} aria-invalid={reasonError ? "true" : "false"}
                                        aria-describedby={reasonError ? "far-exception-reason-error" : undefined} />
                                    { reasonError ? <p className={classes.error} id="far-exception-reason-error" role="alert">{reasonError}</p> : null }
                                </div>
                                <div className={classes.actionsRow}>
                                    <button type="button" className={acknowledgeMode ? classes.primary : classes.secondary} onClick={() => submit(acknowledgeMode ? "acknowledged" : "authorized")}>
                                        {acknowledgeMode ? "Acknowledge and Send" : "Authorize and Continue"}
                                    </button>
                                </div>
                            </div>
                            {
                                allowIgnore ?
                                    <div className={classes.option}>
                                        <p className={classes.optionTitle}>Ignore and continue</p>
                                        <label className={classes.check}>
                                            <input type="checkbox" checked={confirmIgnore} onChange={(e) => setConfirmIgnore(e.target.checked)} />
                                            I understand these recipients are not authorized for these files. This will be logged.
                                        </label>
                                        <div className={classes.actionsRow}>
                                            <button type="button" className={classes.cancel} disabled={!confirmIgnore} onClick={() => submit("ignored")}>Ignore and Continue</button>
                                        </div>
                                    </div>
                                : null
                            }
                        </>
                    :
                        <FarNotice tone={excludedCount > 0 ? "danger" : "info"}>
                            {
                                excludedCount > 0 && recipients.every(r => excludedRecipients[r] && excludedRecipients[r].action === FAR_EXCLUSION.BLOCK) ?
                                    "Blocked recipients can't be overridden. Remove them to continue."
                                : excludedCount > 0 && pairs.some(p => p.excluded && p.effectiveExclusionAction === FAR_EXCLUSION.ADMIN) && !isAdmin ?
                                    "Only an administrator can authorize sending to an excluded recipient. Remove the blocked recipients to continue, or ask your administrator."
                                : excludedCount > 0 ?
                                    "These recipients can't be authorized. Remove them to continue."
                                : <>{isBlock ? "Sending is blocked for unauthorized recipients. " : ""}Only administrators can authorize exceptions.{ removable.length > 0 ? " Remove the unauthorized recipients to continue." : isLink ? " Send these files by email to authorized recipients instead." : "" }</>
                            }
                        </FarNotice>
                }
            </div>
            <div className={classes.footer}>
                <button type="button" className={classes.cancel} onClick={onClose}>Cancel</button>
            </div>
        </Dialog>
    );
}

FarExceptionModal.defaultProps = { canOverride: false, isAdmin: false, acknowledgeMode: false, transferType: "email" };
FarExceptionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    evaluation: PropTypes.object,
    canOverride: PropTypes.bool,
    isAdmin: PropTypes.bool,
    acknowledgeMode: PropTypes.bool,
    transferType: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onRemoveRecipients: PropTypes.func.isRequired,
    onResolve: PropTypes.func.isRequired
};

export default FarExceptionModal;
